/*
 * The Google profile editor (2026-11-01, owner: "a multi editor for brand accounts only, & a mini version for
 * companies/franchise location with sub locations"). A brand sees every profile of the brand and every location below it,
 * and can change several at once. A location sees its own profile and its sublocations' (like Metairie), one at a time,
 * and can't change the name or phone (the brand sets those, LOCATION_LOCKED_FIELDS).
 *
 * Profiles are reached through the company's Google sign in (Marketing, marketing/signin.ts): its own, or else the
 * nearest company above it.
 * Products are not here: Google has no way for an app to change them. Prices are not offered either (no prices rule).
 */
import { z } from "zod";

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, "Use a time like 09:00.");
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-12-25.");

export const WEEK_DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];
export const WEEK_DAY_WORDS: Record<WeekDay, string> = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

/** One open stretch on a day. A day with none is closed; a day can have two (lunch break). */
export const hoursRow = z.object({ day: z.enum(WEEK_DAYS), open: hhmm, close: hhmm });
export type HoursRow = z.infer<typeof hoursRow>;

/** A holiday or other one-off day: closed, or open other hours. */
export const specialDay = z.object({
  date: isoDate,
  closed: z.boolean(),
  open: hhmm.nullable(),
  close: hhmm.nullable(),
});
export type SpecialDay = z.infer<typeof specialDay>;

/** A service on the profile. key is Google's (its service type) or ours for one typed in; new ones have key "new". */
export const gbpService = z.object({
  key: z.string().max(300),
  name: z.string().trim().min(1).max(140),
  description: z.string().trim().max(300),
  /** Typed in (free form), so its name can change. Google's own service types keep theirs. */
  custom: z.boolean(),
});
export type GbpService = z.infer<typeof gbpService>;

export const gbpProfileRow = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  companyName: z.string(),
  subLocation: z.string().nullable(),
  placeId: z.string(),
  /** Read through the Google sign in. False shows only what Places gave (name, address, rating). */
  reachable: z.boolean(),
  title: z.string(),
  address: z.string(),
  phone: z.string(),
  website: z.string(),
  description: z.string(),
  category: z.string(),
  hours: z.array(hoursRow),
  specialHours: z.array(specialDay),
  services: z.array(gbpService),
  /** Google is still checking a change. */
  pending: z.boolean(),
  /** Google changed something itself (from a suggestion). The profile shows Google's. */
  googleUpdated: z.boolean(),
  mapsUrl: z.string().nullable(),
  rating: z.number().nullable(),
  reviewCount: z.number().int(),
  /** Why it could not be read, in plain words, or null. */
  error: z.string().nullable(),
});
export type GbpProfileRow = z.infer<typeof gbpProfileRow>;

/** The Google sign in that reaches these profiles (Marketing, Sign in with Google): this company's or one above it. */
export const gbpSignedIn = z.object({
  signedIn: z.boolean(),
  who: z.string().nullable(),
  /** Made on this company (false: a brand above it). */
  own: z.boolean(),
  /** Google took the sign in back: sign in again. */
  signedOut: z.boolean(),
});
export type GbpSignedIn = z.infer<typeof gbpSignedIn>;

export const gbpProfilesAnswer = z.object({
  /** brand: every profile below, several at once. location: its own and its sublocations'. hq: none. */
  mode: z.enum(["brand", "location", "hq"]),
  link: gbpSignedIn,
  profiles: z.array(gbpProfileRow),
  /** Where a profile can be added: this company and (for a brand) every one below, each with its sublocations. */
  places: z.array(z.object({ companyId: z.uuid(), name: z.string(), subLocations: z.array(z.string()) })),
});
export type GbpProfilesAnswer = z.infer<typeof gbpProfilesAnswer>;

/** One business profile the sign in manages. attached says which company here has it. */
export const googleLocationRow = z.object({
  name: z.string(),
  title: z.string(),
  address: z.string(),
  placeId: z.string().nullable(),
  attached: z.object({ companyId: z.uuid(), name: z.string(), subLocation: z.string().nullable() }).nullable(),
});
export type GoogleLocationRow = z.infer<typeof googleLocationRow>;

export const gbpAttachBody = z.object({
  name: z.string().regex(/^accounts\/[^/]+\/locations\/[^/]+$/),
  companyId: z.uuid(),
  subLocation: z.string().trim().min(1).max(60).nullable(),
});

const website = z.union([
  z.literal(""),
  z
    .url()
    .max(500)
    .regex(/^https:\/\//i, "Use an https:// address."),
]);
const phone = z.string().trim().max(30);

export const gbpPatchBody = z
  .object({
    title: z.string().trim().min(2).max(100).optional(),
    phone: phone.optional(),
    website: website.optional(),
    description: z.string().trim().max(750).optional(),
    hours: z.array(hoursRow).max(21).optional(),
    specialHours: z.array(specialDay).max(60).optional(),
    services: z.array(gbpService).max(100).optional(),
  })
  .strict();
export type GbpPatch = z.infer<typeof gbpPatchBody>;

/** Several profiles at once (brands only). Holiday days are added (a day already there is replaced), the rest replace. */
export const gbpBulkBody = z.object({
  ids: z.array(z.uuid()).min(1).max(100),
  set: z
    .object({
      hours: z.array(hoursRow).max(21).optional(),
      specialHours: z.array(specialDay).min(1).max(60).optional(),
      website: website.optional(),
    })
    .strict()
    .refine((s) => s.hours !== undefined || s.specialHours !== undefined || s.website !== undefined, "Pick what to change."),
});
export type GbpBulk = z.infer<typeof gbpBulkBody>;

export const gbpBulkAnswer = z.object({
  results: z.array(z.object({ id: z.uuid(), title: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type GbpBulkAnswer = z.infer<typeof gbpBulkAnswer>;

/** Problems with hours before they go to Google (each stretch opens before it closes, no overlaps on a day). */
export function hoursProblems(rows: readonly HoursRow[]): string[] {
  const out: string[] = [];
  for (const d of WEEK_DAYS) {
    const day = rows.filter((r) => r.day === d).sort((a, b) => a.open.localeCompare(b.open));
    for (const [i, r] of day.entries()) {
      if (r.close <= r.open) out.push(`${WEEK_DAY_WORDS[d]} closes before it opens.`);
      const next = day[i + 1];
      if (next && next.open < r.close) out.push(`${WEEK_DAY_WORDS[d]} has two times that overlap.`);
    }
  }
  return out;
}

export function specialProblems(days: readonly SpecialDay[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const d of days) {
    if (seen.has(d.date)) out.push(`${d.date} is in the list twice.`);
    seen.add(d.date);
    if (!d.closed && (!d.open || !d.close || d.close <= d.open)) out.push(`${d.date} needs an open and a close time.`);
  }
  return out;
}

/** "Mon to Fri 8 AM to 5 PM, Sat 9 AM to 1 PM, Sun closed" style, short, for a table. */
export function hoursWords(rows: readonly HoursRow[]): string {
  if (!rows.length) return "No hours";
  const t = (s: string) => {
    const [hh, mm] = s.split(":").map(Number) as [number, number];
    const h = hh % 12 === 0 ? 12 : hh % 12;
    return `${String(h)}${mm ? `:${String(mm).padStart(2, "0")}` : ""} ${hh < 12 || hh === 24 ? "AM" : "PM"}`;
  };
  const day = (d: WeekDay) =>
    rows
      .filter((r) => r.day === d)
      .map((r) => `${t(r.open)} to ${t(r.close)}`)
      .join(", ") || "closed";
  const parts: { from: WeekDay; to: WeekDay; words: string }[] = [];
  for (const d of WEEK_DAYS) {
    const w = day(d);
    const last = parts.at(-1);
    if (last?.words === w) last.to = d;
    else parts.push({ from: d, to: d, words: w });
  }
  const short = (d: WeekDay) => WEEK_DAY_WORDS[d].slice(0, 3);
  return parts.map((p) => `${p.from === p.to ? short(p.from) : `${short(p.from)} to ${short(p.to)}`} ${p.words}`).join(", ");
}
